import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Form, Table } from 'react-bootstrap';
import airbnbApi from './api';
import { ErrorAlert, PageLoader, StatusBadge } from './common';

const PAGE_SIZES = [25, 50, 100];

// Live NextPax property index for a property manager
// (GET /content/properties/index), with each row matched to its RedChannel
// Airbnb property when one exists. NextPax gives no total count, so paging is
// previous/next.
const NextpaxProperties = () => {
  const { id } = useParams();
  const [pm, setPm] = useState(null);
  const [page, setPage] = useState(null);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    airbnbApi.getPropertyManager(id).then(setPm).catch(setError);
  }, [id]);

  const load = useCallback(() => {
    setPage(null);
    setError(null);
    airbnbApi.nextpaxProperties(id, { limit, offset }).then(setPage).catch(setError);
  }, [id, limit, offset]);

  useEffect(() => { load(); }, [load]);

  const changeLimit = value => {
    setLimit(value);
    setOffset(0);
  };

  const rows = page ? page.data : [];
  const pageNumber = Math.floor(offset / limit) + 1;

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13">
        <Link to="/airbnb">Property managers</Link> / <Link to={`/airbnb/property-managers/${id}`}>{pm ? pm.company_name : 'Property manager'}</Link>
        {' / '}NextPax properties
      </div>
      <div className="d-flex justify-content-between align-items-center margbot15">
        <h4 className="mb-0">
          NextPax properties {pm && pm.nextpax_code && <span className="fnt14 text-muted">{pm.nextpax_code}</span>}
        </h4>
        <Button size="sm" variant="outline-secondary" onClick={load}>Refresh</Button>
      </div>
      <p className="fnt13 text-muted">
        Live list from NextPax. "Airbnb" shows what RedChannel knows for properties it onboarded; rows not
        onboarded through RedChannel have no local record.
      </p>
      < div className="table-responsive">
        <ErrorAlert error={error} />
        {!page && !error && <PageLoader />}
        {page && rows.length === 0 && <div className="pad20 tc text-muted">No properties on this page.</div>}
        {page && rows.length > 0 && (
          <Table bordered hover size="sm" className="bg1 align-middle">
            <thead>
              <tr>
                <th>Supplier ID</th>
                <th>NextPax property ID</th>
                <th>Status</th>
                <th>Parent</th>
                <th>RedChannel property</th>
                <th>Classification</th>
                <th>Airbnb listing ID</th>
                <th>Airbnb</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.property_id}>
                  <td><code>{row.supplier_property_id}</code></td>
                  <td className="fnt12">{row.property_id}</td>
                  <td><Badge bg={row.status === 'active' ? 'success' : 'secondary'}>{row.status}</Badge></td>
                  <td className="fnt12">
                    {row.parent_id ? (row.parent_supplier_property_id || row.parent_id) : '—'}
                  </td>
                  <td>
                    {row.local
                      ? <Link to={`/airbnb/properties/${row.local.id}`}>{row.local.name || `#${row.local.id}`}</Link>
                      : <span className="text-muted">not in RedChannel</span>}
                  </td>
                  <td>{row.local ? row.local.classification : '—'}</td>
                  <td>{(row.local && row.local.airbnb_listing_id) || '—'}</td>
                  <td>
                    {!row.local && '—'}
                    {row.local && (row.local.airbnb_enabled
                      ? <><Badge bg="success">enabled</Badge> <StatusBadge status={row.local.airbnb_build_status} /></>
                      : <Badge bg="light" text="dark">not enabled</Badge>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {page && (
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="outline-primary" disabled={offset === 0}
              onClick={() => setOffset(Math.max(offset - limit, 0))}>‹ Previous</Button>
            <span className="fnt13">Page {pageNumber}{rows.length > 0 && ` · rows ${offset + 1}–${offset + rows.length}`}</span>
            <Button size="sm" variant="outline-primary" disabled={!page.has_more}
              onClick={() => setOffset(offset + limit)}>Next ›</Button>
            <Form.Select size="sm" style={{ width: 110 }} value={limit} onChange={e => changeLimit(Number(e.target.value))}>
              {PAGE_SIZES.map(size => <option key={size} value={size}>{size} / page</option>)}
            </Form.Select>
          </div>
        )}
      </div>
    </div>
  );
};

export default NextpaxProperties;
